// 待办事项 Supabase 存储服务（Web 应用版本）
import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';
import { getCurrentUser } from '../auth/userService';
import type { Todo } from '@ext-bookmark/shared/types';
import { getWithCache } from '../cache/cacheStrategy';
import { getCache } from '../cache/cacheManager';
import { ensurePersonalGroup } from '../group/groupService';
import { createNotification } from '../notifications/notificationService';

// 个人分组ID缓存（避免重复查询）
const personalGroupIdCache = new Map<string, string>();

export interface TodoSyncError {
  message: string;
  code?: string;
}

/** 可选参数：noCache 为 true 时跳过 getWithCache，供 React Query queryFn 使用 */
export type GetTodosOptions = { noCache?: boolean };

/** 将 Supabase todos 表的一行转为 Todo，供 Realtime 等单条更新使用 */
export function mapSupabaseRowToTodo(item: any): Todo {
  return {
    id: item.id,
    title: item.title,
    description: item.description || undefined,
    completed: item.completed || false,
    completedAt: item.completed_at ? new Date(item.completed_at).getTime() : undefined,
    startDate: new Date(item.start_date).getTime(),
    endDate: item.end_date ? new Date(item.end_date).getTime() : undefined,
    recurrence: item.recurrence || undefined,
    reminder: item.reminder || undefined,
    fontColor: item.font_color || undefined,
    createdAt: new Date(item.created_at).getTime(),
    updatedAt: new Date(item.updated_at).getTime(),
    groupId: item.group_id || undefined,
    assignedTo: item.assigned_to && Array.isArray(item.assigned_to) ? item.assigned_to : undefined,
    createdBy: item.created_by || undefined,
  };
}

/**
 * 从 Supabase 获取所有待办事项（包括个人分组和普通分组）
 * RLS 策略会自动过滤用户有权访问的待办事项
 * 使用新的缓存策略：先返回缓存，异步更新；noCache 时纯拉取（供 React Query 使用）
 */
export async function getTodosFromSupabase(
  onUpdate?: (todos: Todo[]) => void,
  options?: GetTodosOptions
): Promise<{ todos: Todo[]; error: TodoSyncError | null }> {
  if (!isSupabaseAvailable()) {
    return {
      todos: [],
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      todos: [],
      error: { message: '用户未登录' },
    };
  }

  const fetchTodos = async (): Promise<Todo[]> => {
    if (!supabase) throw new Error('Supabase 未配置');
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      console.error('[Todo Sync] 获取待办事项失败:', { error: error.message, code: error.code });
      throw new Error(error.message || '获取待办事项失败');
    }
    return (data || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      completed: item.completed || false,
      completedAt: item.completed_at ? new Date(item.completed_at).getTime() : undefined,
      startDate: new Date(item.start_date).getTime(),
      endDate: item.end_date ? new Date(item.end_date).getTime() : undefined,
      recurrence: item.recurrence || undefined,
      reminder: item.reminder || undefined,
      fontColor: item.font_color || undefined,
      createdAt: new Date(item.created_at).getTime(),
      updatedAt: new Date(item.updated_at).getTime(),
      groupId: item.group_id || undefined,
      assignedTo: item.assigned_to && Array.isArray(item.assigned_to) ? item.assigned_to : undefined,
      createdBy: item.created_by || undefined,
    }));
  };

  if (options?.noCache) {
    try {
      const todos = await fetchTodos();
      return { todos, error: null };
    } catch (error: any) {
      return { todos: [], error: { message: error.message || '获取待办事项时发生未知错误' } };
    }
  }

  const cacheKey = 'todos_cache';
  try {
    const todos = await getWithCache<Todo[]>(cacheKey, fetchTodos, onUpdate);
    return { todos, error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 获取待办事项异常:', error);
    const cached = await getCache<Todo[]>(cacheKey);
    if (cached) {
      if (onUpdate) onUpdate(cached.data);
      return { todos: cached.data, error: null };
    }
    return {
      todos: [],
      error: { message: error.message || '获取待办事项时发生未知错误' },
    };
  }
}

/**
 * 获取个人分组待办事项
 * noCache 时纯拉取（供 React Query 使用）
 */
export async function getPersonalTodosFromSupabase(
  onUpdate?: (todos: Todo[]) => void,
  options?: GetTodosOptions
): Promise<{ todos: Todo[]; error: TodoSyncError | null }> {
  if (!isSupabaseAvailable()) {
    return { todos: [], error: { message: 'Supabase 未配置' } };
  }
  const user = await getCurrentUser();
  if (!user) {
    return { todos: [], error: { message: '用户未登录' } };
  }

  const fetchPersonal = async (): Promise<Todo[]> => {
    if (!supabase) throw new Error('Supabase 未配置');
    let personalGroupId: string | undefined = personalGroupIdCache.get(user.id);
    if (!personalGroupId) {
      const { group, error: groupError } = await ensurePersonalGroup(user.id);
      if (groupError || !group) return [];
      personalGroupId = group.id;
      if (personalGroupId) personalGroupIdCache.set(user.id, personalGroupId);
    }
    if (!personalGroupId) return [];
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('group_id', personalGroupId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message || '获取个人待办事项失败');
    return (data || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      completed: item.completed || false,
      completedAt: item.completed_at ? new Date(item.completed_at).getTime() : undefined,
      startDate: new Date(item.start_date).getTime(),
      endDate: item.end_date ? new Date(item.end_date).getTime() : undefined,
      recurrence: item.recurrence || undefined,
      reminder: item.reminder || undefined,
      fontColor: item.font_color || undefined,
      createdAt: new Date(item.created_at).getTime(),
      updatedAt: new Date(item.updated_at).getTime(),
      groupId: item.group_id || undefined,
      assignedTo: item.assigned_to && Array.isArray(item.assigned_to) ? item.assigned_to : undefined,
      createdBy: item.created_by || undefined,
    }));
  };

  if (options?.noCache) {
    try {
      const todos = await fetchPersonal();
      return { todos, error: null };
    } catch (error: any) {
      return { todos: [], error: { message: error.message || '获取个人待办事项时发生未知错误' } };
    }
  }

  const cacheKey = 'personal_todos_cache';
  try {
    const todos = await getWithCache<Todo[]>(cacheKey, fetchPersonal, onUpdate);
    return { todos, error: null };
  } catch (error: any) {
    const cached = await getCache<Todo[]>(cacheKey);
    if (cached) {
      if (onUpdate) onUpdate(cached.data);
      return { todos: cached.data, error: null };
    }
    return { todos: [], error: { message: error.message || '获取个人待办事项时发生未知错误' } };
  }
}

/**
 * 获取分组待办事项
 * noCache 时纯拉取（供 React Query 使用）
 */
export async function getGroupTodosFromSupabase(
  groupId: string,
  onUpdate?: (todos: Todo[]) => void,
  options?: GetTodosOptions
): Promise<{ todos: Todo[]; error: TodoSyncError | null }> {
  if (!isSupabaseAvailable()) {
    return { todos: [], error: { message: 'Supabase 未配置' } };
  }
  const user = await getCurrentUser();
  if (!user) {
    return { todos: [], error: { message: '用户未登录' } };
  }

  const fetchGroup = async (): Promise<Todo[]> => {
    if (!supabase) throw new Error('Supabase 未配置');
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .eq('group_id', groupId)
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message || '获取分组待办事项失败');
    return (data || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      completed: item.completed || false,
      completedAt: item.completed_at ? new Date(item.completed_at).getTime() : undefined,
      startDate: new Date(item.start_date).getTime(),
      endDate: item.end_date ? new Date(item.end_date).getTime() : undefined,
      recurrence: item.recurrence || undefined,
      reminder: item.reminder || undefined,
      fontColor: item.font_color || undefined,
      createdAt: new Date(item.created_at).getTime(),
      updatedAt: new Date(item.updated_at).getTime(),
      groupId: item.group_id || undefined,
      assignedTo: item.assigned_to && Array.isArray(item.assigned_to) ? item.assigned_to : undefined,
      createdBy: item.created_by || undefined,
    }));
  };

  if (options?.noCache) {
    try {
      const todos = await fetchGroup();
      return { todos, error: null };
    } catch (error: any) {
      return { todos: [], error: { message: error.message || '获取分组待办事项时发生未知错误' } };
    }
  }

  const cacheKey = `group_todos_cache_${groupId}`;
  try {
    const todos = await getWithCache<Todo[]>(cacheKey, fetchGroup, onUpdate);
    return { todos, error: null };
  } catch (error: any) {
    const cached = await getCache<Todo[]>(cacheKey);
    if (cached) {
      if (onUpdate) onUpdate(cached.data);
      return { todos: cached.data, error: null };
    }
    return { todos: [], error: { message: error.message || '获取分组待办事项时发生未知错误' } };
  }
}

/**
 * 获取分配给我的待办事项（包括个人分组和普通分组）
 */
export async function getMyAssignedTodosFromSupabase(): Promise<{ todos: Todo[]; error: TodoSyncError | null }> {
  if (!supabase) {
    return {
      todos: [],
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      todos: [],
      error: { message: '用户未登录' },
    };
  }

  try {
    // 使用 PostgREST 的 cs (contains) 操作符查询 assigned_to 包含当前用户ID的待办事项
    // cs 操作符检查数组是否包含指定值
    const { data, error } = await supabase
      .from('todos')
      .select('*')
      .filter('assigned_to', 'cs', `{${user.id}}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[Todo Sync] 获取分配给我的待办事项失败:', error);
      return {
        todos: [],
        error: {
          message: error.message || '获取分配给我的待办事项失败',
          code: error.code,
        },
      };
    }

    const todos: Todo[] = (data || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      description: item.description || undefined,
      completed: item.completed || false,
      completedAt: item.completed_at ? new Date(item.completed_at).getTime() : undefined,
      startDate: new Date(item.start_date).getTime(),
      endDate: item.end_date ? new Date(item.end_date).getTime() : undefined,
      recurrence: item.recurrence || undefined,
      reminder: item.reminder || undefined,
      fontColor: item.font_color || undefined,
      createdAt: new Date(item.created_at).getTime(),
      updatedAt: new Date(item.updated_at).getTime(),
      groupId: item.group_id || undefined,
      assignedTo: item.assigned_to && Array.isArray(item.assigned_to) ? item.assigned_to : undefined,
      createdBy: item.created_by || undefined,
    }));

    return { todos, error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 获取分配给我的待办事项异常:', error);
    return {
      todos: [],
      error: {
        message: error.message || '获取分配给我的待办事项时发生未知错误',
      },
    };
  }
}

export type SaveTodoToSupabaseOptions = {
  /** 仅更新完成状态等简单字段时设为 true，跳过保存前的 GET 查询，避免勾选时多一次请求与闪烁 */
  skipExistingFetch?: boolean;
};

/**
 * 保存待办事项到 Supabase
 */
export async function saveTodoToSupabase(
  todo: Todo,
  options?: SaveTodoToSupabaseOptions
): Promise<{ todo: Todo | null; error: TodoSyncError | null }> {
  if (!supabase) {
    return {
      todo: null,
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      todo: null,
      error: { message: '用户未登录' },
    };
  }

  try {
    const now = new Date().toISOString();

    // 查询现有任务（用于检测分配变化）；仅更新完成状态时跳过，避免勾选时多一次 GET 与界面闪烁
    let oldTodo: Todo | null = null;
    if (!options?.skipExistingFetch) {
      try {
        const { data: existingData } = await supabase
          .from('todos')
          .select('*')
          .eq('id', todo.id)
          .single();

        if (existingData) {
          oldTodo = {
            id: existingData.id,
            title: existingData.title,
            description: existingData.description || undefined,
            completed: existingData.completed || false,
            completedAt: existingData.completed_at ? new Date(existingData.completed_at).getTime() : undefined,
            startDate: new Date(existingData.start_date).getTime(),
            endDate: existingData.end_date ? new Date(existingData.end_date).getTime() : undefined,
            recurrence: existingData.recurrence || undefined,
            reminder: existingData.reminder || undefined,
            fontColor: existingData.font_color || undefined,
            createdAt: new Date(existingData.created_at).getTime(),
            updatedAt: new Date(existingData.updated_at).getTime(),
            groupId: existingData.group_id || undefined,
            assignedTo: existingData.assigned_to && Array.isArray(existingData.assigned_to) ? existingData.assigned_to : undefined,
            createdBy: existingData.created_by || undefined,
          };
        }
      } catch (err) {
        // 如果查询失败，可能是新任务，继续执行
      }
    }

    // 转换 Todo 格式到数据库格式
    const dbData: any = {
      id: todo.id,
      user_id: user.id,
      title: todo.title,
      description: todo.description || null,
      completed: todo.completed || false,
      completed_at: todo.completedAt ? new Date(todo.completedAt).toISOString() : null,
      start_date: new Date(todo.startDate).toISOString(),
      end_date: todo.endDate ? new Date(todo.endDate).toISOString() : null,
      recurrence: todo.recurrence || null,
      reminder: todo.reminder || null,
      font_color: todo.fontColor || null,
      group_id: todo.groupId || null,
      assigned_to: todo.assignedTo && todo.assignedTo.length > 0 ? todo.assignedTo : [],
      created_by: todo.createdBy || user.id, // 如果没有指定创建者，使用当前用户
      updated_at: now,
    };

    // 如果是新待办事项，设置 created_at
    if (!todo.createdAt) {
      dbData.created_at = now;
    } else {
      dbData.created_at = new Date(todo.createdAt).toISOString();
    }

    const { data, error } = await supabase
      .from('todos')
      .upsert(dbData, {
        onConflict: 'id',
      })
      .select()
      .single();

    if (error) {
      console.error('[Todo Sync] 保存待办事项失败:', error);
      return {
        todo: null,
        error: {
          message: error.message || '保存待办事项失败',
          code: error.code,
        },
      };
    }

    // 转换回 Todo 格式
    const savedTodo: Todo = {
      id: data.id,
      title: data.title,
      description: data.description || undefined,
      completed: data.completed || false,
      completedAt: data.completed_at ? new Date(data.completed_at).getTime() : undefined,
      startDate: new Date(data.start_date).getTime(),
      endDate: data.end_date ? new Date(data.end_date).getTime() : undefined,
      recurrence: data.recurrence || undefined,
      reminder: data.reminder || undefined,
      fontColor: data.font_color || undefined,
      createdAt: new Date(data.created_at).getTime(),
      updatedAt: new Date(data.updated_at).getTime(),
      groupId: data.group_id || undefined,
      assignedTo: data.assigned_to && Array.isArray(data.assigned_to) ? data.assigned_to : undefined,
      createdBy: data.created_by || undefined,
    };

    // 检测任务分配变化，发送通知
    if (savedTodo.assignedTo && savedTodo.assignedTo.length > 0) {
      const oldAssignedTo: string[] = oldTodo?.assignedTo || [];
      const newAssignedTo: string[] = savedTodo.assignedTo;
      
      // 找出新分配的用户
      const newlyAssigned = newAssignedTo.filter((userId: string) => !oldAssignedTo.includes(userId));
      
      if (newlyAssigned.length > 0) {
        // 获取分组信息（如果有）
        let groupName = '';
        if (savedTodo.groupId) {
          try {
            const { data: groupData } = await supabase
              .from('groups')
              .select('name')
              .eq('id', savedTodo.groupId)
              .single();
            groupName = groupData?.name || '';
          } catch (err) {
            // 忽略错误
          }
        }
        
        // 获取分配者信息
        const assignerId = user.id;
        let assignerName = '用户';
        try {
          const { data: assignerProfile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', assignerId)
            .single();
          assignerName = assignerProfile?.username || '用户';
        } catch (err) {
          // 忽略错误
        }
        
        // 为每个新分配的用户创建通知
        for (const userId of newlyAssigned) {
          const groupInfo = groupName ? `分组"${groupName}"中的` : '';
          await createNotification(
            userId,
            'todo_assigned',
            '任务分配',
            `${assignerName} 将${groupInfo}任务"${savedTodo.title}"分配给您`,
            {
              todo_id: savedTodo.id,
              group_id: savedTodo.groupId,
              inviter_id: assignerId,
            }
          ).catch((err) => {
            // 通知创建失败不影响主流程，只记录错误
            console.error('[Todo Sync] 创建任务分配通知失败:', err);
          });
        }
      }
    }

    return { todo: savedTodo, error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 保存待办事项异常:', error);
    return {
      todo: null,
      error: {
        message: error.message || '保存待办事项时发生未知错误',
      },
    };
  }
}

/**
 * 从 Supabase 删除待办事项
 */
export async function deleteTodoFromSupabase(todoId: string): Promise<{ error: TodoSyncError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    // RLS 策略会自动检查权限，不需要手动添加 user_id 条件
    const { error } = await supabase
      .from('todos')
      .delete()
      .eq('id', todoId);

    if (error) {
      console.error('[Todo Sync] 删除待办事项失败:', {
        error: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        todoId,
      });
      return {
        error: {
          message: error.message || '删除待办事项失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 删除待办事项异常:', error);
    return {
      error: {
        message: error.message || '删除待办事项时发生未知错误',
      },
    };
  }
}

/**
 * 批量删除待办事项
 */
export async function deleteTodosFromSupabase(todoIds: string[]): Promise<{ error: TodoSyncError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    // RLS 策略会自动检查权限，不需要手动添加 user_id 条件
    const { error } = await supabase
      .from('todos')
      .delete()
      .in('id', todoIds);

    if (error) {
      console.error('[Todo Sync] 批量删除待办事项失败:', error);
      return {
        error: {
          message: error.message || '批量删除待办事项失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 批量删除待办事项异常:', error);
    return {
      error: {
        message: error.message || '批量删除待办事项时发生未知错误',
      },
    };
  }
}

/**
 * 同步待办事项到 Supabase（批量）
 */
export async function syncTodosToSupabase(todos: Todo[]): Promise<{ error: TodoSyncError | null }> {
  if (!supabase) {
    return {
      error: { message: 'Supabase 未配置' },
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: { message: '用户未登录' },
    };
  }

  try {
    const now = new Date().toISOString();
    
    // 转换所有待办事项到数据库格式
    const dbData = todos.map((todo) => ({
      id: todo.id,
      user_id: user.id,
      title: todo.title,
      description: todo.description || null,
      completed: todo.completed || false,
      completed_at: todo.completedAt ? new Date(todo.completedAt).toISOString() : null,
      start_date: new Date(todo.startDate).toISOString(),
      end_date: todo.endDate ? new Date(todo.endDate).toISOString() : null,
      recurrence: todo.recurrence || null,
      reminder: todo.reminder || null,
      font_color: todo.fontColor || null,
      group_id: todo.groupId || null,
      assigned_to: todo.assignedTo && todo.assignedTo.length > 0 ? todo.assignedTo : [],
      created_by: todo.createdBy || user.id,
      created_at: new Date(todo.createdAt).toISOString(),
      updated_at: now,
    }));

    // 使用 upsert 批量更新
    const { error } = await supabase
      .from('todos')
      .upsert(dbData, {
        onConflict: 'id',
      });

    if (error) {
      console.error('[Todo Sync] 同步待办事项失败:', {
        error: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
        todosCount: todos.length,
      });
      return {
        error: {
          message: error.message || '同步待办事项失败',
          code: error.code,
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    console.error('[Todo Sync] 同步待办事项异常:', error);
    return {
      error: {
        message: error.message || '同步待办事项时发生未知错误',
      },
    };
  }
}
